import { repApi, qs } from './repApi';
import { gridWindowQuery } from './gridWindow';
import type { GridWindow, GridWindowArgs } from './gridWindow';
import type {
  Calibration,
  CalibrationBody,
  Equipment,
  HistoryEntry,
  NotificationBody,
  NotificationContact,
  Organization,
} from '@/types';

export const equipmentApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    /** One page of the Equipment table (legacy MuiTableGrid window). */
    equipmentWindow: builder.query<GridWindow<Equipment>, GridWindowArgs>({
      query: (args) => `/equipment/grid${gridWindowQuery(args)}`,
      providesTags: ['Equipment'],
    }),
    /** One page of the Calibration Due Dates list. */
    calibrationWindow: builder.query<GridWindow<Calibration>, GridWindowArgs>({
      query: (args) => `/calibration/grid${gridWindowQuery(args)}`,
      providesTags: ['Calibration'],
    }),
    unassignedEquipment: builder.query<Equipment[], { county?: number } | void>({
      query: (arg) => `/equipment/unassigned${qs({ county: arg ? arg.county : undefined })}`,
      providesTags: ['EquipmentUnassigned'],
    }),
    organizations: builder.query<Organization[], void>({
      query: () => '/equipment/organizations',
      keepUnusedDataFor: 600,
      providesTags: ['Organizations'],
    }),
    getEquipment: builder.query<Equipment, number>({
      query: (id) => `/equipment/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Equipment', id }],
    }),
    equipmentHistory: builder.query<HistoryEntry[], number>({
      query: (id) => `/equipment/${id}/history`,
      keepUnusedDataFor: 0,
    }),
    createEquipment: builder.mutation<Equipment, Partial<Equipment>>({
      query: (body) => ({ url: '/equipment', method: 'POST', body }),
      invalidatesTags: ['Equipment', 'EquipmentUnassigned', 'Redistribution'],
    }),
    updateEquipment: builder.mutation<Equipment, { id: number; body: Partial<Equipment> }>({
      query: ({ id, body }) => ({ url: `/equipment/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Equipment', id },
        'EquipmentUnassigned',
        'Redistribution',
      ],
    }),
    deleteEquipment: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/equipment/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Equipment', id },
        'EquipmentUnassigned',
        'Redistribution',
      ],
    }),
    // Calibration dates
    // The entry carries its equipment's own fields (serial number, model, tag), which the
    // Equipment form changes without touching this tag, so each visit loads it fresh.
    getCalibration: builder.query<Calibration, number>({
      query: (id) => `/calibration/${id}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, id) => [{ type: 'Calibration', id }],
    }),
    calibrationHistory: builder.query<HistoryEntry[], number>({
      query: (id) => `/calibration/${id}/history`,
      keepUnusedDataFor: 0,
    }),
    createCalibration: builder.mutation<Calibration, CalibrationBody>({
      query: (body) => ({ url: '/calibration', method: 'POST', body }),
      invalidatesTags: ['Calibration', 'EquipmentUnassigned', 'Equipment'],
    }),
    updateCalibration: builder.mutation<Calibration, { id: number; body: CalibrationBody }>({
      query: ({ id, body }) => ({ url: `/calibration/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Calibration', id },
        'EquipmentUnassigned',
        'Equipment',
      ],
    }),
    deleteCalibration: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/calibration/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Calibration', id },
        'EquipmentUnassigned',
        'Equipment',
      ],
    }),
    // Notifications (per organization; triggers = organization names)
    equipmentNotifications: builder.query<
      NotificationContact[],
      { organizationId?: number } | void
    >({
      query: (arg) =>
        `/equipment/notifications${qs({ organizationId: arg ? arg.organizationId : undefined })}`,
      providesTags: ['EquipmentNotifications'],
    }),
    getEquipmentNotification: builder.query<
      NotificationContact,
      { id: number; organizationId?: number }
    >({
      query: ({ id, organizationId }) => `/equipment/notifications/${id}${qs({ organizationId })}`,
      providesTags: (_r, _e, { id }) => [{ type: 'EquipmentNotifications', id }],
    }),
    createEquipmentNotification: builder.mutation<
      NotificationContact,
      { organizationId?: number; body: NotificationBody }
    >({
      query: ({ organizationId, body }) => ({
        url: `/equipment/notifications${qs({ organizationId })}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EquipmentNotifications'],
    }),
    updateEquipmentNotification: builder.mutation<
      NotificationContact,
      { id: number; organizationId?: number; body: NotificationBody }
    >({
      query: ({ id, organizationId, body }) => ({
        url: `/equipment/notifications/${id}${qs({ organizationId })}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        'EquipmentNotifications',
        { type: 'EquipmentNotifications', id },
      ],
    }),
    deleteEquipmentNotification: builder.mutation<
      { pvDataID: number },
      { id: number; organizationId?: number }
    >({
      query: ({ id, organizationId }) => ({
        url: `/equipment/notifications/${id}${qs({ organizationId })}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EquipmentNotifications'],
    }),
  }),
});

export const {
  useEquipmentWindowQuery,
  useLazyEquipmentWindowQuery,
  useCalibrationWindowQuery,
  useUnassignedEquipmentQuery,
  useLazyUnassignedEquipmentQuery,
  useOrganizationsQuery,
  useGetEquipmentQuery,
  useEquipmentHistoryQuery,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useGetCalibrationQuery,
  useCalibrationHistoryQuery,
  useCreateCalibrationMutation,
  useUpdateCalibrationMutation,
  useDeleteCalibrationMutation,
  useEquipmentNotificationsQuery,
  useGetEquipmentNotificationQuery,
  useCreateEquipmentNotificationMutation,
  useUpdateEquipmentNotificationMutation,
  useDeleteEquipmentNotificationMutation,
} = equipmentApi;
